/** Adds a script to the head once: one already holding `id`, or this `src`, counts as loaded. */
export const loadScript = (
  url: string,
  id?: string,
  attributes: Partial<Pick<HTMLScriptElement, 'async' | 'crossOrigin' | 'defer' | 'type'>> = {}
): Promise<HTMLScriptElement> =>
  new Promise((resolve, reject) => {
    const existing = (id && document.getElementById(id)) || document.querySelector(`script[src="${url}"]`);
    if (existing) {
      resolve(existing as HTMLScriptElement);
      return;
    }

    const script = document.createElement('script');
    script.onload = () => resolve(script);
    script.onerror = () => {
      script.remove();
      reject(new Error(`Could not load ${url}`));
    };
    if (id) script.id = id;
    Object.assign(script, attributes);
    script.src = url;
    document.head.appendChild(script);
  });

/** Adds a stylesheet to the head, resolving once it has loaded. */
export const loadStyle = (url: string): Promise<HTMLLinkElement> =>
  new Promise((resolve, reject) => {
    const style = document.createElement('link');
    style.rel = 'stylesheet';
    style.onload = () => resolve(style);
    style.onerror = () => reject(new Error(`Could not load ${url}`));
    style.href = url;
    document.head.appendChild(style);
  });

/** Runs `fn` once the page has loaded and the browser is idle; Safari has no requestIdleCallback. */
export const whenIdle = (fn: () => void) => {
  const idle = () =>
    'requestIdleCallback' in window ? requestIdleCallback(fn, { timeout: 3000 }) : setTimeout(fn, 1000);
  if (document.readyState === 'complete') idle();
  else window.addEventListener('load', idle, { once: true });
};
